import React from 'react';
import { StyleSheet } from 'react-native';
import { DNAModal, DNABox, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { DNAChip, DNAModalVariant, DNAThumbnail } from 'src/components/DNA';
import { useDispatch } from 'src/state/redux';
import { DocumentVersionORM } from 'src/types/orms';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { getSlideTitle, getThumbURL } from 'src/utils/thumbnailHelpers';
import { ContentPageData } from 'src/hooks/useContentPageData/useContentPageData';

const style = StyleSheet.create({
  container: {
    padding: 9,
    paddingTop: 0,
  },
  thumbnailContainer: {
    minWidth: 700,
    minHeight: 396,
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
  },
  textColor: {
    color: colors['color-gray-400'],
  },
})

type PreviousSlidePreviewProps = {
  previousDocumentVersionORM?: DocumentVersionORM,
  previousPageId?: string,
  previousPageNum?: number
  latestPublishedContentPageData?: ContentPageData[],
}

type NewSlidePreviewProps = {
  newDocumentVersionORM?: DocumentVersionORM,
  newPageNum?: number,
  latestContentPageData?: ContentPageData[],
  currentDocumentVersionORM?: DocumentVersionORM,
}

type MatchSlidesPreviewModalProps = {
  previousDocumentVersionORM?: DocumentVersionORM,
  previousPageNum?: number,
  newDocumentVersionORM?: DocumentVersionORM,
  newPageNum?: number,
  latestContentPageData?: ContentPageData[],
  latestPublishedContentPageData?: ContentPageData[],
  currentDocumentVersionORM?: DocumentVersionORM,
}

const PreviousSlidePreview: React.FC<PreviousSlidePreviewProps> = (props) => {
  const {
    previousDocumentVersionORM,
    previousPageNum,
    latestPublishedContentPageData,
  } = props

  if (!previousDocumentVersionORM || !previousPageNum || !latestPublishedContentPageData ) return null

  const previousVersionThumbURL = getThumbURL(previousDocumentVersionORM, previousPageNum)
  const previousTitle = getSlideTitle(
    previousDocumentVersionORM,
    previousPageNum,
    latestPublishedContentPageData,
  )

  return (
    <DNABox appearance="col" spacing="sm">
      <DNABox alignX="center" appearance="col" spacing="sm">
        <DNABox appearance="row" spacing="sm" alignY="center">
          <DNAText bold style={style.textColor}>
            Version {previousDocumentVersionORM?.meta.version.semVerLabel}
          </DNAText>
          <DNABox style={{ paddingVertical: 8 }}>
            <DNAChip.Beacon appearance="filled" children="CURRENT" />
          </DNABox>
        </DNABox>
        <DNABox style={style.thumbnailContainer}>
          <DNAThumbnail
            key={previousDocumentVersionORM?.model.id}
            s3URL={previousVersionThumbURL}
            size="xxl"
          />
        </DNABox>
      </DNABox>
      <DNABox alignX="start">
        <DNAText bold style={style.textColor}>{previousPageNum}. {previousTitle}</DNAText>
      </DNABox>
    </DNABox>
  )
}

const NewSlidePreview: React.FC<NewSlidePreviewProps> = (props) => {
  const {
    newDocumentVersionORM,
    newPageNum,
    latestContentPageData,
    currentDocumentVersionORM,
  } = props

  if (!newDocumentVersionORM || !newPageNum || !latestContentPageData) return null

  const newVersionThumbURL = getThumbURL(newDocumentVersionORM, newPageNum)
  const newTitle = getSlideTitle(newDocumentVersionORM, newPageNum, latestContentPageData)

  return (
    <DNABox appearance="col" spacing="sm">
      <DNABox alignX="center" appearance="col" spacing="sm">
        <DNABox appearance="row" spacing="sm" alignY="center">
          <DNAText bold style={style.textColor}>
            {`${!currentDocumentVersionORM || currentDocumentVersionORM?.meta.version.updateStatus === 'NOT_PUBLISHED'
              ? 'New version'
              : currentDocumentVersionORM?.meta.version.semVerLabel}`}
          </DNAText>
          <DNABox style={{ paddingVertical: 8 }}>
            <DNAChip.Beacon appearance="filled" status="success" children="NEW" />
          </DNABox>
        </DNABox>
        <DNABox style={style.thumbnailContainer}>
          <DNAThumbnail
            key={newDocumentVersionORM?.model.id}
            s3URL={newVersionThumbURL}
            size="xxl"
          />
        </DNABox>
      </DNABox>
      <DNABox alignX="start">
        <DNAText bold style={style.textColor}>{newPageNum}. {newTitle}</DNAText>
      </DNABox>
    </DNABox>
  )
}

export const MatchSlidesPreviewModal: DNAModalVariant<MatchSlidesPreviewModalProps> = (props) => {
  const {
    previousDocumentVersionORM,
    currentDocumentVersionORM,
    previousPageNum,
    latestPublishedContentPageData,
    newDocumentVersionORM,
    newPageNum,
    latestContentPageData,
  } = props

  const dispatch = useDispatch();

  function closeModal(): void {
    dispatch(DNAModalActions.closeModal());
  }

  return (
    <DNAModal>
      <DNABox alignX="end">
        <DNAModal.Header onClose={closeModal} />
      </DNABox>
      <DNAModal.Body>
        <DNABox alignX="start" appearance="col" spacing="sm" fill style={style.container}>
          <DNABox appearance="row" spacing="xs">
            <PreviousSlidePreview
              previousDocumentVersionORM={previousDocumentVersionORM}
              previousPageNum={previousPageNum}
              latestPublishedContentPageData={latestPublishedContentPageData}
            />
            <NewSlidePreview
              newDocumentVersionORM={newDocumentVersionORM}
              newPageNum={newPageNum}
              latestContentPageData={latestContentPageData}
              currentDocumentVersionORM={currentDocumentVersionORM}
            />
          </DNABox>
        </DNABox>
      </DNAModal.Body>
    </DNAModal>
  )
}

export default MatchSlidesPreviewModal
